import React, { useState } from 'react';
import { Music, Disc } from 'lucide-react';
import GlitchText from './GlitchText';

const AlbumCard = ({ title, image, platforms, gridClass, albumId }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`album-card relative overflow-hidden border border-white/10 ${gridClass} group cursor-pointer`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`album-card-${albumId}`}
    >
      <div className="absolute inset-0">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
      </div>

      <div className="relative z-10 h-full min-h-[250px] md:min-h-[300px] flex flex-col justify-end p-6">
        <div className={`absolute top-6 right-6 transition-all duration-500 ${isHovered ? 'opacity-100 rotate-0' : 'opacity-30 rotate-12'}`}>
          {platforms.some(p => p.name === 'SoundCloud') ? (
            <Music className="w-6 h-6 text-white/60" />
          ) : (
            <Disc className="w-6 h-6 text-white/60" />
          )}
        </div>

        <h3 
          className="font-heading text-2xl md:text-3xl font-bold text-white tracking-wider mb-4"
          data-testid={`album-title-${albumId}`}
        >
          <GlitchText>{title}</GlitchText>
        </h3>

        <div 
          className={`flex flex-wrap gap-4 transition-all duration-500 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          {platforms.map((platform, idx) => (
            <a
              key={idx}
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              className="platform-link font-mono text-xs"
              data-testid={`platform-link-${albumId}-${idx}`}
              onClick={(e) => e.stopPropagation()}
            >
              {platform.name}
            </a>
          ))}
        </div>
      </div>

      <div 
        className={`absolute inset-0 border-2 border-white/0 transition-all duration-500 pointer-events-none ${isHovered ? 'border-white/20' : ''}`}
      />
    </div>
  );
};

const AlbumGrid = () => {
  return (
    <section className="albums-section" data-testid="albums-section">
      <h2 className="section-title font-accent animate-fade-in tracking-[0.4em]">
        <GlitchText>DISCOGRAPHY</GlitchText>
      </h2>
      
      <div className="albums-grid">
        <AlbumCard 
          albumId={1}
          title="POPSOUL"
          image="https://images.unsplash.com/photo-1581084349663-7ab88c58d362?q=80&w=800"
          platforms={[
            { name: 'Apple Music', url: 'https://music.apple.com/us/album/p-o-p-s-o-u-l/1831383127' },
            { name: 'Spotify', url: 'https://open.spotify.com/album/7HaBmqduTUT5SE20PT6LF2' }
          ]}
          gridClass="md:col-span-2 md:row-span-2"
        />
        
        <AlbumCard 
          albumId={2}
          title="Underground Pop"
          image="https://images.unsplash.com/photo-1590509018522-93c01e069373?q=80&w=800"
          platforms={[
            { name: 'Apple Music', url: 'https://music.apple.com/us/album/underground-pop/1661853859' },
            { name: 'Spotify', url: 'https://open.spotify.com/album/6Iov7p4X44ChICLVVD0K1b' }
          ]}
          gridClass="md:col-span-1 md:row-span-1"
        />
        
        <AlbumCard 
          albumId={3}
          title="S3: The Final Straw"
          image="https://images.unsplash.com/photo-1637750586228-179a1a7aef84?q=80&w=800"
          platforms={[
            { name: 'Apple Music', url: 'https://music.apple.com/us/album/s3-the-final-straw/1646704448' },
            { name: 'Spotify', url: 'https://open.spotify.com/album/0ByqU3DuNojlWD7NQLB425' }
          ]}
          gridClass="md:col-span-1 md:row-span-2"
        />
        
        <AlbumCard 
          albumId={4}
          title="Behind The Scenes Rhy"
          image="https://images.unsplash.com/photo-1597524115557-3b88520704c2?q=80&w=800"
          platforms={[
            { name: 'SoundCloud', url: 'https://on.soundcloud.com/brVEHes1pAxvpo7POR' }
          ]}
          gridClass="md:col-span-1 md:row-span-1"
        />
      </div>

      {/* Phase 2 Visuals Teaser */}
      <div className="mt-8 flex justify-center">
        <div 
          className="px-8 py-4 bg-white/5 border border-white/10 backdrop-blur-sm"
          data-testid="phase2-teaser"
        >
          <span className="font-accent text-sm tracking-[0.3em] text-red-500/70 uppercase">
            VISUALS UNLOCKS IN PHASE 2
          </span>
        </div>
      </div>
    </section>
  );
};

export default AlbumGrid;
